import { api } from "./apiClient.js";

/*
 * Service layer — a direct port of frontend/app/services/api.js.
 *
 * Every path is unchanged, so the backend needs no work for the redesign. Kept as
 * thin one-liners on purpose: the wrappers exist so a URL appears exactly once in
 * the codebase, not to add behaviour. Response shaping belongs in the query hooks
 * (src/hooks/), error handling in the interceptor.
 *
 * The 16 groups mirror the original file so the port can be diffed against it.
 */

/* ---- Auth ---------------------------------------------------------------- */
export const authService = {
    login: (credentials) => api.post("/api/auth/login", credentials),
    getProfile: () => api.get("/api/auth/profile"),
    registerDesktop: (data) => api.post("/api/auth/register-desktop", data),
    changePassword: (data) => api.post("/api/auth/change-password", data),
};

/* ---- Organizations ------------------------------------------------------- */
export const organizationService = {
    createOrganization: (data) => api.post("/api/organizations", data),
    getOrganization: (id) => api.get(`/api/organizations/${id}`),
    updateOrganization: (id, data) => api.patch(`/api/organizations/${id}`, data),
    getAllOrganizations: () => api.get("/api/organizations"),
    validateS3Config: (s3Data) => api.post("/api/organizations/validate-s3", s3Data),
    updateS3Config: (organizationId, s3Data) =>
        api.patch(`/api/organizations/${organizationId}/s3-config`, s3Data),
};

/* ---- Users -------------------------------------------------------------- */
export const userService = {
    getAllUsers: (params) => api.get("/api/users", { params }),
    getUser: (id) => api.get(`/api/users/${id}`),
    createUser: (data) => api.post("/api/users", data),
    updateUser: (id, data) => api.patch(`/api/users/${id}`, data),
    deleteUser: (id) => api.delete(`/api/users/${id}`),
};

/* ---- Time entries ------------------------------------------------------- */
export const timeEntryService = {
    getUserTimeEntries: (userId, params) => api.get(`/api/time-entries/user/${userId}`, { params }),
    getMyTimeEntries: (params) => api.get("/api/time-entries/my-entries", { params }),
    getTodayEntries: (params) => api.get("/api/time-entries/today", { params }),
    getTimeEntry: (id) => api.get(`/api/time-entries/${id}`),
    getWorkHoursTrend: (params) => api.get("/api/time-entries/trend", { params }),
    createTimeEntry: (data) => api.post("/api/time-entries", data),
    endTimeEntry: (id, data) => api.post(`/api/time-entries/${id}/end`, data),
    updateTimeEntry: (id, data) => api.patch(`/api/time-entries/${id}`, data),
};

/* ---- Activities --------------------------------------------------------- */
export const activityService = {
    getUserActivities: (userId, params) => api.get(`/api/activities/user/${userId}`, { params }),
    getTimeEntryActivities: (timeEntryId) => api.get(`/api/activities/time-entry/${timeEntryId}`),
    createActivity: (data) => api.post("/api/activities", data),
};

/* ---- Screenshots -------------------------------------------------------- */
export const screenshotService = {
    getOrganizationScreenshots: (params) => api.get("/api/screenshots/organization/all", { params }),
    getUserScreenshots: (userId, params) => api.get(`/api/screenshots/user/${userId}`, { params }),
    getTimeEntryScreenshots: (timeEntryId) => api.get(`/api/screenshots/time-entry/${timeEntryId}`),
    getScreenshot: (id) => api.get(`/api/screenshots/${id}`),
    createScreenshot: (data) => api.post("/api/screenshots", data),
};

/* ---- Idle times --------------------------------------------------------- */
export const idleTimeService = {
    getUserIdleTimes: (userId, params) => api.get(`/api/idle-times/user/${userId}`, { params }),
    getTimeEntryIdleTimes: (timeEntryId) => api.get(`/api/idle-times/time-entry/${timeEntryId}`),
    getTodayIdleTimes: (params) => api.get("/api/idle-times/today", { params }),
    startIdleTime: (data) => api.post("/api/idle-times/start", data),
    endIdleTime: (idleTimeId, data) => api.patch(`/api/idle-times/${idleTimeId}/end`, data),
};

/* ---- Usage / activity dashboard ----------------------------------------- */
export const usageService = {
    getAllUsersActivityDashboard: (params, config) =>
        api.get("/api/usage/all-users-activity-dashboard", { params, ...config }),
    getAllUsersTimeEntryStats: (params) => api.get("/api/usage/all-users-time-entry-stats", { params }),
    getUserUsageStats: (userId, params) => api.get(`/api/usage/user/${userId}`, { params }),
    // Every session of one application or website for one user — what the
    // UserDetail page's app modal opens. Was missing from the original port
    // because the old page built the URL by hand instead of going through the
    // service layer.
    getAppSessions: (userId, params) => api.get(`/api/usage/app-sessions/${userId}`, { params }),
    getMyUsageStats: (params) => api.get("/api/usage/my-stats", { params }),
    getTodayUsage: (params) => api.get("/api/usage/today", { params }),
    getTimeEntryUsage: (timeEntryId) => api.get(`/api/usage/time-entry/${timeEntryId}`),
    getOrganizationTopApps: (params) => api.get("/api/usage/organization/top-apps", { params }),
    getAppUsers: (params) => api.get("/api/usage/organization/app-users", { params }),
    createUsage: (data) => api.post("/api/usage", data),
};

/* ---- Reports ------------------------------------------------------------ */
export const reportService = {
    getSavedReports: (params) => api.get("/api/reports", { params }),
    getReport: (id) => api.get(`/api/reports/${id}`),
    deleteReport: (id) => api.delete(`/api/reports/${id}`),

    getLiveActivity: (params) => api.get("/api/reports/live-activity", { params }),
    getTodayBreaks: (params) => api.get("/api/reports/break/today", { params }),

    // One generator per report type. The Reports page's registry maps its keys to
    // these, so adding a report is a config change plus one endpoint.
    application: (data) => api.post("/api/reports/application", data),
    website: (data) => api.post("/api/reports/website", data),
    attendance: (data) => api.post("/api/reports/attendance", data),
    timesheet: (data) => api.post("/api/reports/timesheet", data),
    idle: (data) => api.post("/api/reports/idle", data),
    break: (data) => api.post("/api/reports/break", data),
    team: (data) => api.post("/api/reports/team", data),
    comparison: (data) => api.post("/api/reports/comparison", data),
    trend: (data) => api.post("/api/reports/trend", data),
    productivity: (data) => api.post("/api/reports/productivity", data),
};

/* ---- Exports ------------------------------------------------------------ */
export const exportService = {
    getHistory: (params) => api.get("/api/export/history", { params }),
    getDownloadLink: (data) => api.post("/api/export/download-link", data),
    emailExport: (data) => api.post("/api/export/email", data),
};

/* ---- Productivity rules ------------------------------------------------- */
export const productivityRuleService = {
    getRules: (params) => api.get("/api/productivity-rules", { params }),
    getRule: (id) => api.get(`/api/productivity-rules/${id}`),
    createRule: (data) => api.post("/api/productivity-rules", data),
    updateRule: (id, data) => api.patch(`/api/productivity-rules/${id}`, data),
    deleteRule: (id) => api.delete(`/api/productivity-rules/${id}`),
    bulkImport: (data) => api.post("/api/productivity-rules/bulk-import", data),
};

/* ---- Teams -------------------------------------------------------------- */
export const teamService = {
    getOrganizationTeams: (params) => api.get("/api/teams", { params }),
    getTeam: (id) => api.get(`/api/teams/${id}`),
    createTeam: (data) => api.post("/api/teams", data),
    updateTeam: (id, data) => api.patch(`/api/teams/${id}`, data),
    deleteTeam: (id) => api.delete(`/api/teams/${id}`),
    addTeamMembers: (id, data) => api.post(`/api/teams/${id}/members`, data),
    // A DELETE with a body — axios needs it under `data`, not as the second arg.
    removeTeamMembers: (id, data) => api.delete(`/api/teams/${id}/members`, { data }),
};

/* ---- Projects ----------------------------------------------------------- */
export const projectService = {
    getProjects: (params) => api.get("/api/projects", { params }),
    getProjectHours: (params) => api.get("/api/projects/hours", { params }),
    getProject: (id) => api.get(`/api/projects/${id}`),
    createProject: (data) => api.post("/api/projects", data),
    updateProject: (id, data) => api.patch(`/api/projects/${id}`, data),
    archiveProject: (id, data) => api.patch(`/api/projects/${id}/archive`, data),
    deleteProject: (id) => api.delete(`/api/projects/${id}`),
};

/* ---- Tracking schedules ------------------------------------------------- */
export const trackingScheduleService = {
    getSchedules: (params) => api.get("/api/tracking-schedules", { params }),
    getSchedule: (id) => api.get(`/api/tracking-schedules/${id}`),
    getCurrentSchedule: (params) => api.get("/api/tracking-schedules/current", { params }),
    getStats: (params) => api.get("/api/tracking-schedules/stats", { params }),
    createSchedule: (data) => api.post("/api/tracking-schedules", data),
    updateSchedule: (id, data) => api.put(`/api/tracking-schedules/${id}`, data),
    toggleStatus: (id, data) => api.patch(`/api/tracking-schedules/${id}/toggle-status`, data),
    deleteSchedule: (id) => api.delete(`/api/tracking-schedules/${id}`),
};

/* ---- Super admin -------------------------------------------------------- */
export const superAdminService = {
    getOverview: () => api.get("/api/super-admin/overview"),
    getAnalytics: (params) => api.get("/api/super-admin/analytics", { params }),

    getOrganizations: (params) => api.get("/api/super-admin/organizations", { params }),
    getOrganization: (organizationId) =>
        api.get(`/api/super-admin/organizations/${organizationId}`),
    createOrganization: (data) => api.post("/api/super-admin/organizations", data),
    updateOrganization: (organizationId, data) =>
        api.patch(`/api/super-admin/organizations/${organizationId}`, data),
    toggleOrganizationStatus: (organizationId, data) =>
        api.patch(`/api/super-admin/organizations/${organizationId}/toggle-status`, data),
    deleteOrganization: (organizationId) =>
        api.delete(`/api/super-admin/organizations/${organizationId}`),

    getUsers: (params) => api.get("/api/super-admin/users", { params }),
    createUser: (data) => api.post("/api/super-admin/users", data),
    updateUser: (userId, data) => api.patch(`/api/super-admin/users/${userId}`, data),

    getAllPackages: () => api.get("/api/super-admin/packages"),
    createPackage: (data) => api.post("/api/super-admin/packages", data),
    updatePackage: (packageId, data) => api.patch(`/api/super-admin/packages/${packageId}`, data),
    deletePackage: (packageId) => api.delete(`/api/super-admin/packages/${packageId}`),

    validateS3Config: (data) => api.post("/api/super-admin/validate-s3", data),
};

/*
 * Desktop-agent build catalog (Super Admin). Uploads are multipart/form-data —
 * pass a FormData and axios sets the boundary; `config` carries onUploadProgress.
 * The desktop agent hits /latest + /:id/download itself, not from here.
 */
export const appVersionService = {
    list: () => api.get("/api/app-version"),
    upload: (formData, config) => api.post("/api/app-version", formData, config),
    setLatest: (id) => api.patch(`/api/app-version/${id}/latest`),
    remove: (id) => api.delete(`/api/app-version/${id}`),
    getSettings: () => api.get("/api/app-version/settings"),
    updateSettings: (payload) => api.put("/api/app-version/settings", payload),
    latestVersion: () => api.get("/api/app-version/latest-version"),
};

/* ---- Packages ----------------------------------------------------------- */
export const packageService = {
    getPackages: () => api.get("/api/packages"),
    getPackage: (packageId) => api.get(`/api/packages/${packageId}`),
};
